import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faFloppyDisk} from "@fortawesome/free-solid-svg-icons";

import { APIMS } from "../../../../../../../../../config/apims";
import { APISD } from "../../../../../../../../../config/apisd";
import ToastError from "../../../../../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../../../../../NotificationToast/ToastSuccess";


const modalStyles = `
  .fase-grade-modal.module-modal-dialog {
    width: calc(100% - 28px);
    max-width: 920px;
  }

  .fase-grade-info {
    padding: 13px 15px;
    border: 1px solid #dfe7ff;
    border-radius: 11px;
    background: #f5f7ff;
    color: #405071;
    font-size: 13px;
    line-height: 1.6;
  }

  .fase-grade-section {
    margin-top: 20px;
  }

  .fase-grade-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 10px;
  }

  .fase-grade-toolbar .module-form-label {
    margin-bottom: 0;
  }

  .fase-grade-select-all {
    padding: 4px 0;
    border: 0;
    background: transparent;
    color: #3f5bea;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
  }

  .fase-grade-select-all:hover {
    color: #2f53ad;
    text-decoration: underline;
  }

  .fase-grade-helper {
    display: flex;
    min-height: 54px;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
    padding: 14px;
    border: 1px dashed #dce3f0;
    border-radius: 10px;
    background: #fafbfe;
    color: #8791a5;
    font-size: 13px;
    text-align: center;
  }

  .fase-grade-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    margin-top: 10px;
  }

  .fase-grade-card {
    position: relative;
    display: flex;
    min-width: 0;
    align-items: flex-start;
    gap: 10px;
    padding: 13px;
    border: 1px solid #e2e7f0;
    border-radius: 11px;
    background: #ffffff;
    cursor: pointer;
    transition:
      border-color 0.2s ease,
      background-color 0.2s ease,
      box-shadow 0.2s ease,
      transform 0.2s ease;
  }

  .fase-grade-card:hover {
    border-color: #b9c9f5;
    box-shadow: 0 8px 20px rgba(55, 83, 151, 0.09);
    transform: translateY(-1px);
  }

  .fase-grade-card.is-selected {
    border-color: #6a84dc;
    background: #f4f7ff;
    box-shadow: 0 0 0 2px rgba(67, 104, 197, 0.08);
  }

  .fase-grade-card.is-disabled {
    border-color: #e6e9ef;
    background: #f8f9fb;
    cursor: not-allowed;
    opacity: 0.68;
    box-shadow: none;
    transform: none;
  }

  .fase-grade-card input {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    margin-top: 2px;
    accent-color: #3f5bea;
    cursor: pointer;
  }

  .fase-grade-card.is-disabled input {
    cursor: not-allowed;
  }

  .fase-grade-card-content {
    display: block;
    min-width: 0;
  }

  .fase-grade-card-title {
    display: block;
    color: #24314e;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.45;
  }

  .fase-grade-card-subtitle {
    display: block;
    margin-top: 4px;
    color: #758099;
    font-size: 11px;
    line-height: 1.45;
    white-space: normal;
    overflow-wrap: break-word;
  }

  .fase-grade-badge {
    display: inline-flex;
    align-items: center;
    margin-top: 8px;
    padding: 3px 8px;
    border-radius: 999px;
    background: #fff0f0;
    color: #c24a4a;
    font-size: 10px;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    .fase-grade-list {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 520px) {
    .fase-grade-modal.module-modal-dialog {
      width: calc(100% - 18px);
      margin: 9px auto;
    }

    .fase-grade-toolbar {
      align-items: flex-start;
      flex-direction: column;
      gap: 5px;
    }

    .fase-grade-list {
      grid-template-columns: 1fr;
    }
  }
`;

const normalizeValue = (value) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .toUpperCase();

const getGradeId = (item) =>
  item?.ysb_grade_level_id || item?.grade_level_id || item?.id || "";

const getBranchCode = (item) =>
  item?.ysb_branch_id ||
  item?.branch_code ||
  item?.branch_id ||
  item?.branch ||
  "";

const getSchoolName = (item) =>
  item?.name_school || item?.school_name || item?.ysb_school_id || "-";

export default function ModalFaseGradeAdd(props) {
  const token = localStorage.getItem("token");
  const storageLevel = localStorage.getItem("level") || "developer";
  const idUser = localStorage
    .getItem("id_admin")
    ?.replace(/[|&;$%@"<>()+,]/g, "");

  const [branches, setBranches] = useState([]);
  const [allGradeLevels, setAllGradeLevels] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedGradeIds, setSelectedGradeIds] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  const existingGradeIdSet = useMemo(
    () =>
      new Set(
        (props?.existingGradeLevels || [])
          .map((item) => normalizeValue(getGradeId(item)))
          .filter(Boolean)
      ),
    [props?.existingGradeLevels]
  );

  const loadMasterData = useCallback(async () => {
    setLoadingMaster(true);

    const branchRequestBody = {
      page: 1,
      limit: 1000,
      ascending: 1,
      search: "",
    };

    const gradeRequestBody = {
      page: 1,
      limit: 10000,
      ascending: 1,
      search: "",
      level: storageLevel,
    };

    try {
      const [branchResponse, gradeResponse] = await Promise.all([
        APIMS.get("/api/branches", {
          ...fetchParams,
          params: branchRequestBody,
          data: branchRequestBody,
        }),
        APIMS.get("/api/grade-levels", {
          ...fetchParams,
          params: gradeRequestBody,
          data: gradeRequestBody,
        }),
      ]);

      const branchRows = branchResponse?.data?.data || [];
      const gradeRows = gradeResponse?.data?.data || [];

      setBranches(
        [...branchRows].sort((a, b) =>
          String(
            a?.branch_name || a?.name_branch || a?.branch_code || ""
          ).localeCompare(
            String(
              b?.branch_name || b?.name_branch || b?.branch_code || ""
            ),
            "id",
            { numeric: true, sensitivity: "base" }
          )
        )
      );

      setAllGradeLevels(
        [...gradeRows].sort((a, b) => {
          const branchCompare = String(getBranchCode(a)).localeCompare(
            String(getBranchCode(b)),
            "id",
            { numeric: true, sensitivity: "base" }
          );

          if (branchCompare !== 0) {
            return branchCompare;
          }

          const schoolCompare = String(getSchoolName(a)).localeCompare(
            String(getSchoolName(b)),
            "id",
            { numeric: true, sensitivity: "base" }
          );

          if (schoolCompare !== 0) {
            return schoolCompare;
          }

          return String(a?.name_level || "").localeCompare(
            String(b?.name_level || ""),
            "id",
            { numeric: true, sensitivity: "base" }
          );
        })
      );
    } catch (error) {
      setBranches([]);
      setAllGradeLevels([]);

      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal mengambil data cabang dan tingkatan",
      });
    } finally {
      setLoadingMaster(false);
    }
  }, [fetchParams, storageLevel]);

  useEffect(() => {
    if (!props?.show) {
      return;
    }

    setSelectedBranch("");
    setSelectedGradeIds([]);
    setIsSubmitting(false);
    loadMasterData();
  }, [loadMasterData, props?.show]);

  const filteredGradeLevels = useMemo(() => {
    if (!selectedBranch) {
      return [];
    }

    return allGradeLevels.filter(
      (grade) =>
        normalizeValue(getBranchCode(grade)) ===
        normalizeValue(selectedBranch)
    );
  }, [allGradeLevels, selectedBranch]);

  const selectableGradeLevels = useMemo(
    () =>
      filteredGradeLevels.filter(
        (grade) =>
          !existingGradeIdSet.has(normalizeValue(getGradeId(grade)))
      ),
    [existingGradeIdSet, filteredGradeLevels]
  );

  const isAllSelectableChecked = useMemo(() => {
    if (selectableGradeLevels.length === 0) {
      return false;
    }

    return selectableGradeLevels.every((grade) =>
      selectedGradeIds.some(
        (selectedId) =>
          normalizeValue(selectedId) === normalizeValue(getGradeId(grade))
      )
    );
  }, [selectableGradeLevels, selectedGradeIds]);

  const handleBranchChange = (event) => {
    setSelectedBranch(event.target.value);
    setSelectedGradeIds([]);
  };

  const handleGradeCheck = (gradeId) => {
    const normalizedGradeId = normalizeValue(gradeId);

    if (existingGradeIdSet.has(normalizedGradeId)) {
      return;
    }

    setSelectedGradeIds((previous) => {
      const alreadySelected = previous.some(
        (id) => normalizeValue(id) === normalizedGradeId
      );

      if (alreadySelected) {
        return previous.filter(
          (id) => normalizeValue(id) !== normalizedGradeId
        );
      }

      return [...previous, gradeId];
    });
  };

  const handleSelectAll = () => {
    if (isAllSelectableChecked) {
      setSelectedGradeIds([]);
      return;
    }

    setSelectedGradeIds(
      selectableGradeLevels.map((grade) => getGradeId(grade)).filter(Boolean)
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    if (!props?.faseId) {
      ToastError.fire({
        icon: "error",
        title: "ID fase belum tersedia",
      });
      return;
    }

    if (!selectedBranch) {
      ToastError.fire({
        icon: "error",
        title: "Cabang wajib dipilih",
      });
      return;
    }

    if (selectedGradeIds.length === 0) {
      ToastError.fire({
        icon: "error",
        title: "Pilih minimal satu tingkatan",
      });
      return;
    }

    if (!idUser) {
      ToastError.fire({
        icon: "error",
        title: "User ID belum tersedia. Silakan login ulang.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await APISD.post(
        `/api/fases/store-grades/${props.faseId}`,
        {
          ysb_grade_level_ids: selectedGradeIds,
          user_id: idUser,
        },
        fetchParams
      );

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.data?.status === 200 ||
        response?.data?.status === 201
      ) {
        ToastSuccess.fire({
          icon: "success",
          title:
            response?.data?.message ||
            "Tingkatan berhasil ditambahkan ke fase",
        });

        await props?.GetResponseData?.();
        props?.onHide?.();
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error?.response?.data?.message ||
          "Gagal menambahkan tingkatan ke fase",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{modalStyles}</style>

      <Modal
        {...props}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="sims-modal-dialog fase-grade-modal"
        contentClassName="sims-modal-content"
        aria-labelledby="modal-tambah-fase-tingkatan"
      >
        <form onSubmit={handleSubmit}>
          <div className="sims-modal-header">
            <div>
              <h5 id="modal-tambah-fase-tingkatan" className="sims-modal-title">Tambah Tingkatan Fase</h5>
             
            </div>

            <button
              type="button"
              className="sims-modal-close"
              onClick={props?.onHide}
              disabled={isSubmitting}
              aria-label="Tutup modal"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <Modal.Body className="sims-modal-body mt-2 mx-2">
            <div className="fase-grade-info">
              Tingkatan yang dipilih akan dimasukkan ke fase{" "}
              <b>{props?.faseName || "-"}</b>. 
              {/* Tingkatan yang sudah terdaftar
              akan otomatis dinonaktifkan. */}
            </div>

            <div className="fase-grade-section">
              <label
                className="sims-form-label"
                htmlFor="add-fase-grade-branch"
              >
                Cabang <span>*</span>
              </label>

              <select
                id="add-fase-grade-branch"
                name="ysb_branch_id"
                value={selectedBranch}
                onChange={handleBranchChange}
                className="sims-form-input"
                disabled={loadingMaster || isSubmitting}
                required
              >
                <option value="">Pilih Cabang...</option>

                {branches.map((branch, index) => {
                  const branchCode =
                    branch?.branch_code ||
                    branch?.ysb_branch_id ||
                    branch?.id;
                  const branchName =
                    branch?.branch_name ||
                    branch?.name_branch ||
                    branchCode ||
                    "-";

                  return (
                    <option
                      key={branch?.id || branchCode || index}
                      value={branchCode}
                    >
                      {branchName}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="fase-grade-section">
              <div className="fase-grade-toolbar">
                <label className="sims-form-label">
                  Tingkatan <span>*</span>
                </label>

                {selectedBranch && selectableGradeLevels.length > 0 && (
                  <button
                    type="button"
                    className="fase-grade-select-all"
                    onClick={handleSelectAll}
                    disabled={isSubmitting}
                  >
                    {isAllSelectableChecked ? "Batalkan semua" : "Pilih semua"}
                  </button>
                )}
              </div>

              {!selectedBranch ? (
                <div className="fase-grade-helper">
                  Silakan pilih cabang terlebih dahulu.
                </div>
              ) : loadingMaster ? (
                <div className="fase-grade-helper">
                  Memuat data tingkatan...
                </div>
              ) : filteredGradeLevels.length === 0 ? (
                <div className="fase-grade-helper">
                  Tingkatan tidak ditemukan untuk cabang ini.
                </div>
              ) : (
                <div className="fase-grade-list">
                  {filteredGradeLevels.map((grade, index) => {
                    const gradeId = getGradeId(grade);
                    const alreadyAssigned = existingGradeIdSet.has(
                      normalizeValue(gradeId)
                    );
                    const isSelected = selectedGradeIds.some(
                      (selectedId) =>
                        normalizeValue(selectedId) === normalizeValue(gradeId)
                    );

                    return (
                      <label
                        key={gradeId || index}
                        className={`fase-grade-card ${
                          isSelected ? "is-selected" : ""
                        } ${alreadyAssigned ? "is-disabled" : ""}`}
                        title={
                          alreadyAssigned
                            ? "Tingkatan ini sudah terdaftar pada fase"
                            : ""
                        }
                      >
                        <input
                          type="checkbox"
                          checked={alreadyAssigned || isSelected}
                          disabled={alreadyAssigned || isSubmitting}
                          onChange={() => handleGradeCheck(gradeId)}
                        />

                        <span className="fase-grade-card-content">
                          <span className="fase-grade-card-title">
                            Tingkatan {grade?.name_level || "-"}
                          </span>

                          <span className="fase-grade-card-subtitle">
                            {getSchoolName(grade)}
                          </span>

                          {alreadyAssigned && (
                            <span className="fase-grade-badge">
                              Sudah terdaftar
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </Modal.Body>

          <div className="sims-modal-footer">
            <button
              type="button"
              className="sims-modal-cancel"
              onClick={props?.onHide}
              disabled={isSubmitting}
            >
              Batal
            </button>

            <button
              type="submit"
              className="sims-modal-submit"
              disabled={
                isSubmitting ||
                loadingMaster ||
                !selectedBranch ||
                selectedGradeIds.length === 0
              }
            >
              {isSubmitting ? (
                <>
                  <span className="sims-button-spinner" />
                  <span>Menyimpan...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faFloppyDisk}
                  />
                  <span>Tambahkan</span>
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
